import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import type { PlanOption } from '@/@types/trackheros'

type Props = {
    /** The company plan being chosen, or null when closed. */
    plan: PlanOption | null
    onCancel: () => void
    onConfirm: () => void
}

/**
 * Before a freelancer buys Team: what becoming a team account changes.
 *
 * Shared by the plan picker and onboarding's plan step, the two places a plan
 * marked `converts_account` can be chosen. Asked every time, because the change
 * is one-way — a company account cannot go back to a personal plan
 * (`billing.provisioning._refuse_plan_kind_mismatch`) — and because a seat
 * minimum means paying for seats nobody is in yet.
 *
 * The account changes only once Paddle confirms the payment
 * (`paddle_billing._activate`), so cancelling here or closing the checkout
 * afterwards leaves it as it was.
 */
const CompanyPlanConfirm = ({ plan, onCancel, onConfirm }: Props) => {
    const { t } = useTranslation()
    const minSeats = plan?.min_seats ?? 1

    return (
        <ConfirmDialog
            isOpen={Boolean(plan)}
            type="info"
            title={t('billing.company.title')}
            confirmText={t('billing.company.confirm', {
                plan: plan?.name ?? '',
            })}
            onClose={onCancel}
            onRequestClose={onCancel}
            onCancel={onCancel}
            onConfirm={onConfirm}
        >
            <p>{t('billing.company.body', { plan: plan?.name ?? '' })}</p>
            <ul className="mt-3 list-disc space-y-1 ps-5">
                <li>{t('billing.company.invite')}</li>
                <li>
                    {minSeats > 1
                        ? t('billing.company.seatsMin', { n: minSeats })
                        : t('billing.company.seats')}
                </li>
                <li>{t('billing.company.keeps')}</li>
                <li>{t('billing.company.oneWay')}</li>
            </ul>
        </ConfirmDialog>
    )
}

export default CompanyPlanConfirm
